import React, {useMemo} from 'react'
import {useRouteMatch} from 'react-router-dom'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useCustomerServicePage, useCustomUrlMapping} from '../../hooks/useScapiCustomApi'
import CustomerServiceSkeleton from './skeleton'
import PageTypes from './page-types'
import {CustomerServicePageConfigProvider} from './hooks/use-page-config'
import {removeSiteLocaleFromPath} from '../../utils/url'
import PropTypes from 'prop-types'

const usePageConfig = ({pageType, pageId}) => {
    const isUrlMappingNeeded = !(pageType && pageId)

    // this is the route match from the parent route
    const routeMatch = useRouteMatch()

    const urlSegment = useMemo(() => removeSiteLocaleFromPath(routeMatch.url), [routeMatch.url])

    const {data: urlMapping} = useCustomUrlMapping(
        {
            parameters: {
                c_urlSegment: urlSegment
            }
        },
        {
            enabled: isUrlMappingNeeded
        }
    )

    return useCustomerServicePage(
        {
            parameters: !isUrlMappingNeeded
                ? {pageType, pageId}
                : {
                      pageType:
                          urlMapping?.resourceType === 'content_asset'
                              ? 'content'
                              : urlMapping?.resourceType === 'library_folder'
                              ? 'folder'
                              : urlMapping?.resourceType,
                      pageId: urlMapping?.resourceId
                  }
        },
        {
            enabled:
                !isUrlMappingNeeded || Boolean(urlMapping?.resourceType && urlMapping?.resourceId)
        }
    )
}

const CustomerService = ({pageType, pageId}) => {
    const pageConfigQuery = usePageConfig({pageType, pageId})
    const {data: pageConfig, isLoading, isError, error} = pageConfigQuery

    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.customerService) {
            res.set('Cache-Control', cacheConfig.customerService)
        }
    }

    if (isLoading) {
        return <CustomerServiceSkeleton />
    }

    if (isError) {
        return <div>{error.message}</div>
    }

    if (PageTypes[pageConfig?.type]) {
        const {component: Component} =
            // functional pages have an additional level of nesting
            pageConfig.type === 'function'
                ? PageTypes[pageConfig.type][pageConfig.content?.function]
                : PageTypes[pageConfig.type]
        return (
            <CustomerServicePageConfigProvider value={pageConfig}>
                <Component {...pageConfig.content?.params} />
            </CustomerServicePageConfigProvider>
        )
    }

    return (
        <div>
            <h1>
                <span>Not handled page type&nbsp;</span>
                <strong>{pageConfig?.type}</strong>
            </h1>
            <details>
                <summary>query - {pageConfigQuery.status}</summary>
                <pre>{JSON.stringify(pageConfigQuery, null, 2)}</pre>
            </details>
            <details>
                <summary>page config</summary>
                <pre>{JSON.stringify(pageConfig, null, 2)}</pre>
            </details>
        </div>
    )
}

CustomerService.propTypes = {
    pageType: PropTypes.oneOf(['content', 'folder']),
    pageId: PropTypes.string
}

export default CustomerService
